import { ref, provide, inject, computed, Ref } from '@vue/composition-api'
import store from '@/store'
import { getEffectiveDatesRange } from '@/components/composables/useEffectiveDatesRange'
import type { SyllabusDocument } from '@sy/client/exposed/types'
import useSections from '@sy/client/composables/useSections'
import _debounce from 'lodash/debounce'

export const REFERENCE_VALUE_INJECTED_SECTION = 'REFERENCE_VALUE_INJECTED_SECTION'
export const REFERENCE_VALUE_INJECTED_CM_COURSE = 'REFERENCE_VALUE_INJECTED_CM_COURSE'

export default function useReferenceValues () {
  const { getSectionBySectionNumber, fetchSectionsByCmCourse } = useSections()

  const syllabusDocument: Ref<SyllabusDocument> = ref({})
  const effectiveDatesRange = computed(() => getEffectiveDatesRange(syllabusDocument.value))
  const term = computed(() => store.getters.termById(syllabusDocument.value.startTerm))

  const section = computed(() => getSectionBySectionNumber(syllabusDocument.value.section))
  const cmCourse = computed(() => store.getters['curriculumCourses/cmCourseByEffectiveDatesRangeLookup'](
    syllabusDocument.value.course,
    effectiveDatesRange.value
  ))

  function provideReferences () {
    provide(REFERENCE_VALUE_INJECTED_SECTION, section)
    provide(REFERENCE_VALUE_INJECTED_CM_COURSE, cmCourse)
  }

  async function fetchReferences (doc: SyllabusDocument) {
    syllabusDocument.value = doc
    await fetchCmCourse()
    await fetchSmSection()
  }
  const fetchReferencesDebounced = _debounce(fetchReferences, 800)

  function injectReferences () {
    const section = inject(REFERENCE_VALUE_INJECTED_SECTION, {})
    const cmCourse = inject(REFERENCE_VALUE_INJECTED_CM_COURSE, {})
    return {
      section,
      cmCourse,
    }
  }

  function fetchCmCourse () {
    const { course } = syllabusDocument.value
    if (!course) return {}
    // TODO: Check if course for given data already exist in store to stop unnecessary fetch
    return store.dispatch('curriculumCourses/getCourseByCourseGroupId', {
      courseGroupId: course,
      effectiveDatesRange: effectiveDatesRange.value,
    })
  }

  function fetchSmSection () {
    const { section } = syllabusDocument.value
    // TODO: Check if section for given data already exist in store to stop unnecessary fetch
    if (!section || !cmCourse.value || !term.value) return {}
    fetchSectionsByCmCourse({
      course: cmCourse.value,
      term: term.value,
    })
  }

  return {
    // Use to provide
    provideReferences,
    fetchReferences,
    fetchReferencesDebounced,
    // Use in deep nested component
    injectReferences,
  }
}
